import parse from 'html-react-parser';
import { History } from 'history';
import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import { Navigation } from '../../../../global-functions';
import { IAccountUser } from '../../../../models/account-user';
import { RootState } from '../../../../store';
import { Buckets } from '../../../../models/buckets-enum';
import { IOrderNotificationPreference } from '../../../../models/order-notification-preference';
import { setIsBusy } from '../../../../store/api/api-status-action';
import * as companyActions from '../../../../store/company/company-actions';
import * as preferenceActions from '../../../../store/preferences/order-preferences-actions';
import { AuthConsumer } from '../../../authorization/auth-context';
import FormActions from '../../../common/form-actions';
import PageTitle from '../../../common/page.title';
import OrderPreferenceItem from './order-preference-item';
import { getErrorMessage } from '../../../../utils/error-handling';

interface IOrderNotificationPreferencesProps {
    orderPreferences: IOrderNotificationPreference[] | null;
    accountContacts: IAccountUser[] | null;
    history: History;
    location: Location;
    isLoaded: boolean;
    loadOrderPreferences: (id?: string) => Promise<void>;
    loadAccountContacts: (id?: string) => Promise<void>;
    saveOrderPreferences: (
        preferences: IOrderNotificationPreference[],
        id?: string
    ) => Promise<boolean>;
    setIsBusy: (isBusy: boolean, timeout: number) => Promise<void>;
}

export const OrderNotificationPreferences = ({
    orderPreferences,
    accountContacts,
    history,
    location,
    isLoaded,
    loadOrderPreferences,
    loadAccountContacts,
    saveOrderPreferences,
    setIsBusy,
}: IOrderNotificationPreferencesProps) => {
    const intl = useIntl();
    // jest mock fn() requires React.usState signature to work.
    const [orderPreferencesState, setOrderPreferencesState] = useState<
        IOrderNotificationPreference[] | null
    >([]);
    const [accountContactsState, setAccountContactsState] = useState<
        IAccountUser[] | null
    >([]);

    const [companyId, setCompanyId] = useState('');
    const [errorsState, setErrorsState] = useState<{
        errorMessage: string | null;
    }>({ errorMessage: null });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cId = params.get('c') || '';

        loadOrderPreferences(cId);
        loadAccountContacts(cId);

        if (cId) {
            setCompanyId(cId);
        }
    }, []);

    useEffect(() => {
        if (orderPreferences) {
            setOrderPreferencesState(orderPreferences);
        }
    }, [orderPreferences]);

    useEffect(() => {
        if (accountContacts) {
            setAccountContactsState(accountContacts);
        }
    }, [accountContacts]);

    const filterContacts = (
        p: IOrderNotificationPreference
    ): IAccountUser[] => {
        return accountContactsState!.filter(
            (u) =>
                u.shipTo === p.shipTo && u.location === p.shipToAccount.location
        );
    };

    const handleChange = (event: any, p: IOrderNotificationPreference) => {
        const { name, value } = event.target;

        setOrderPreferencesState((prevState) =>
            prevState!.map((s) => {
                if (s === p) {
                    return { ...s, [name]: value, isDirty: true };
                }
                return s;
            })
        );
    };

    const handleCopy = (p: IOrderNotificationPreference) => {
        setIsBusy(true, 500);
        setOrderPreferencesState((prevState) =>
            prevState!.map((s) => {
                if (s !== p) {
                    const contacts = filterContacts(s);

                    if (
                        !contacts.find(
                            (c) => c.directoryUser === p.emailAddress
                        )
                    ) {
                        setAccountContactsState((prevState) => [
                            ...prevState!,
                            {
                                billTo: null,
                                shipTo: s.shipTo,
                                location: s.shipToAccount.location,
                                directoryUser: p.emailAddress,
                                firstName: '',
                                lastName: '',
                            },
                        ]);
                    }

                    return {
                        ...s,
                        emailAddress: p.emailAddress,
                        isDirty: true,
                    };
                }
                return s;
            })
        );
    };

    const handleSave = async () => {
        try {
            const prefs = orderPreferencesState!.filter(
                (x) => x.isDirty // && x.emailAddress
            );
            const ok = await saveOrderPreferences(prefs, companyId);

            if (ok) {
                if (!companyId) {
                    history.push('/my-account/preferences');
                } else {
                    Navigation.pushExternal(
                        `/my-account/manage-companies/manage?c=${companyId}`
                    );
                }
            }
        } catch (error) {
            setErrorsState({ errorMessage: getErrorMessage(error) });
            setIsBusy(false, 1);
        }
    };

    return (
        <>
            {orderPreferencesState &&
                orderPreferencesState.length > 0 &&
                isLoaded &&
                accountContactsState && (
                    <>
                        <AuthConsumer>
                            {({ buckets }) => {
                                const isReadOnly = !buckets.find(
                                    (x) => x === Buckets.Preferences
                                );
                                return (
                                    <>
                                        <PageTitle pageTitleId="Notification_Preferences" />
                                        <h1 className="underline">
                                            <FormattedMessage id="Order_Notification_Preferences" />
                                        </h1>
                                        <p>
                                            <FormattedMessage id="Order_Notification_Preference_Directions_Start" />
                                            &nbsp;
                                            <span
                                                style={{
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                <FormattedMessage id="Order_Notification_Preference_Directions_Middle" />
                                            </span>
                                            &nbsp;
                                            <FormattedMessage id="Order_Notification_Preference_Directions_End" />
                                        </p>
                                        <div className="row">
                                            <div className="col-md-12">
                                                <hr />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-12">
                                                {orderPreferencesState.map(
                                                    (
                                                        p: IOrderNotificationPreference,
                                                        i
                                                    ) => {
                                                        return (
                                                            <div
                                                                className="row"
                                                                key={i}
                                                            >
                                                                <div className="col-md-12">
                                                                    <OrderPreferenceItem
                                                                        isReadOnly={
                                                                            isReadOnly
                                                                        }
                                                                        preference={
                                                                            p
                                                                        }
                                                                        users={filterContacts(
                                                                            p
                                                                        )}
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleChange(
                                                                                e,
                                                                                p
                                                                            )
                                                                        }
                                                                        onCopy={
                                                                            handleCopy
                                                                        }
                                                                        showCopyAllButton={
                                                                            i ===
                                                                            0
                                                                        }
                                                                    />
                                                                    {orderPreferencesState.length >
                                                                        1 &&
                                                                        i <
                                                                            orderPreferencesState.length -
                                                                                1 && (
                                                                            <hr />
                                                                        )}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                        <FormActions
                                            cancelUrl="/my-account/preferences"
                                            hideSave={isReadOnly}
                                            pullRight={true}
                                            handleSave={handleSave}
                                        />
                                    </>
                                );
                            }}
                        </AuthConsumer>
                        {errorsState.errorMessage && (
                            <div className="alert alert-danger gutter-top">
                                <span style={{ whiteSpace: 'pre-wrap' }}>
                                    {parse(
                                        intl
                                            .formatMessage({
                                                id: 'Sorry_an_error_has_occurred',
                                            })
                                            .replace('\\n', ' ')
                                    )}
                                </span>
                            </div>
                        )}
                    </>
                )}
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        orderPreferences: state.orderPreferences
            ? state.orderPreferences.data!
            : null,
        accountContacts: state.accountContacts
            ? state.accountContacts.data!
            : null,
        isLoaded:
            state.orderPreferences !== undefined &&
            state.accountContacts !== undefined,
    };
};

const mapDispatchToProps = {
    loadOrderPreferences: preferenceActions.loadOrderPreferences,
    saveOrderPreferences: preferenceActions.saveOrderPreferences,
    loadAccountContacts: companyActions.loadAccountContacts,
    setIsBusy,
};

const container = connect(
    mapStateToProps,
    mapDispatchToProps
)(React.memo(OrderNotificationPreferences));

export default container;
